import api from "../lib/api";
import { isExcludedDeliveryPincode } from "../config/excludedPincodes";

const INDIA_POST_PINCODE_URL = "https://api.postalpincode.in/pincode";

const DELIVERY_ZONES = {
  north: ["11", "12", "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "23", "24", "25", "26", "27", "28"],
  west: ["30", "31", "32", "33", "34", "36", "37", "38", "39", "40", "41", "42", "43", "44"],
  central: ["45", "46", "47", "48", "49"],
  south: ["50", "51", "52", "53", "56", "57", "58", "59", "60", "61", "62", "63", "64", "67", "68", "69"],
  east: ["70", "71", "72", "73", "74", "75", "76", "77", "78", "79", "80", "81", "82", "83", "84", "85"],
};

const cleanPincode = (pincode) => String(pincode || "").replace(/\D/g, "").slice(0, 6);

const isSequentialPincode = (pincode) => {
  const digits = pincode.split("").map(Number);
  const step = digits[1] - digits[0];
  if (Math.abs(step) !== 1) return false;
  return digits.every((digit, index) => index === 0 || digit - digits[index - 1] === step);
};

const isObviouslyInvalidPincode = (pincode) =>
  /^(\d)\1{5}$/.test(pincode) || isSequentialPincode(pincode);

const assertValidPincode = (pincode) => {
  const clean = cleanPincode(pincode);
  if (!/^[1-8]\d{5}$/.test(clean)) {
    throw new Error("Enter a valid 6-digit Indian pincode.");
  }
  return clean;
};

const zoneFor = (pincode) => {
  const prefix = pincode.slice(0, 2);
  return Object.entries(DELIVERY_ZONES).find(([, prefixes]) => prefixes.includes(prefix))?.[0] || "remote";
};

const deliveryWindowFor = (pincode) => {
  const zone = zoneFor(pincode);
  if (zone === "north" || zone === "west") return [3, 5];
  if (zone === "central" || zone === "south") return [4, 6];
  return [5, 7];
};

const unavailableEstimate = (pincode, source = "frontend-validation") => ({
  available: false,
  pincode,
  city: "",
  state: "",
  message: `Delivery is not available to ${pincode}. Please enter a valid serviceable pincode.`,
  source,
  cod_available: false,
  estimated_min_days: null,
  estimated_max_days: null,
});

const verificationUnavailableEstimate = (pincode, source = "postal-lookup-unavailable") => ({
  available: false,
  pincode,
  city: "",
  state: "",
  message: "We could not verify this pincode right now. Please try again in a moment.",
  source,
  cod_available: false,
  estimated_min_days: null,
  estimated_max_days: null,
});

const isLocallyBlockedPincode = (pincode) =>
  isObviouslyInvalidPincode(pincode) || isExcludedDeliveryPincode(pincode);

const parseIndiaPostResponse = (payload, pincode) => {
  const first = Array.isArray(payload) ? payload[0] : null;
  const status = String(first?.Status || "").toLowerCase();
  const postOffices = Array.isArray(first?.PostOffice) ? first.PostOffice : [];
  const matchingOffices = postOffices.filter((office) => String(office?.Pincode || "").trim() === pincode);
  const postOffice =
    matchingOffices.find((office) => String(office?.DeliveryStatus || "").trim().toLowerCase() === "delivery") ||
    null;

  if (status !== "success" || !postOffice) {
    return {
      available: false,
      pincode,
      city: "",
      state: "",
      message: `Delivery is not available to ${pincode}.`,
      source: "india-post",
      cod_available: false,
      estimated_min_days: null,
      estimated_max_days: null,
    };
  }

  if (isLocallyBlockedPincode(pincode)) {
    return unavailableEstimate(pincode, "excluded-pincode");
  }

  const [estimated_min_days, estimated_max_days] = deliveryWindowFor(pincode);
  return {
    available: true,
    pincode,
    city: postOffice.District || postOffice.Name || "",
    state: postOffice.State || "",
    message: `Delivery available to ${pincode}.`,
    source: "india-post",
    cod_available: true,
    estimated_min_days,
    estimated_max_days,
  };
};

const estimateViaIndiaPost = async (pincode) => {
  const response = await fetch(`${INDIA_POST_PINCODE_URL}/${pincode}`, {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new Error("India Post lookup failed.");
  return parseIndiaPostResponse(await response.json(), pincode);
};

const shouldFallbackFromBackend = (error) => {
  const status = error?.response?.status;
  return !status || status === 404 || status >= 500;
};

export const estimateDeliveryByPincode = async (pincode) => {
  const clean = assertValidPincode(pincode);

  if (isLocallyBlockedPincode(clean)) {
    return unavailableEstimate(clean);
  }

  try {
    const { data } = await api.get("/shipping/estimate", { params: { pincode: clean } });
    if (data?.available && isLocallyBlockedPincode(clean)) {
      return unavailableEstimate(clean, "excluded-pincode");
    }
    if (data?.available && data?.source === "local-fallback") {
      return verificationUnavailableEstimate(clean);
    }
    return data;
  } catch (error) {
    if (!shouldFallbackFromBackend(error)) throw error;
  }

  try {
    return await estimateViaIndiaPost(clean);
  } catch {
    return verificationUnavailableEstimate(clean);
  }
};
